import * as React from 'react';

import styled from 'styled-components';
import { useI18n } from '@oneaudi/i18n-context';
import {
  Damages,
  Dimension,
  VehicleBasic,
  useServicesContext,
  TechDataFootnoteLabel,
  FootnoteReference,
} from '@oneaudi/vtp-shared';
import { trackOwnersManualClick } from './tracking';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { Text, TextLink } from '@oneaudi/unified-web-components';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import { TechdataConfiguration } from './TechnicalDataPdp';
import { FeatureAppConfig } from '../../FeatureHubAppDefinition';
import { Badge, Column, Item, Key, ListContainer, Value } from './TechnicalDataPDP.styles';

export interface TechdataItem {
  key: string;
  name: string;
  src: string;
  value: string;
  details?: string;
}

export interface TechDataListProps {
  techDataKeys: TechdataConfiguration[];
  isInitial: boolean;
  techDataItems: TechdataItem[];
  pollutionBadge?: string;
  damages?: Damages;
  dimensions?: Dimension[];
  vehicle: VehicleBasic;
  showProductSafetyInfo?: boolean;
  dataTestId?: string;
}
const messages = {
  technicalData: {
    pollutionBadge: {
      defaultMessage: 'Pollution badge',
      id: 'nemo.ui.sc.details.technical-data.pollution-badge.label',
    },
    taxBandVed: {
      defaultMessage: 'VED Tax Band',
      id: 'nemo.ui.sc.details.technical-data.tax-band-ved.label',
    },
    exhaust: {
      defaultMessage: 'Exhaust',
      id: 'nemo.ui.sc.details.technical-data.exhaust.label',
    },
    damages: {
      repairs: {
        label: {
          defaultMessage: 'Repairs',
          id: 'nemo.ui.sc.details.technical-data.repairs.label',
        },
        info: {
          defaultMessage:
            'The damage was professionally repaired and already considered priced in the used car offer price.',
          id: 'nemo.ui.sc.details.technical-data.repairs.info',
        },
      },
      defects: {
        label: {
          defaultMessage: 'Defects',
          id: 'nemo.ui.sc.details.technical-data.defects.label',
        },
      },
    },
    dimensions: {
      heightLabel: {
        defaultMessage: 'height',
        id: 'nemo.ui.sc.details.technical-data.dimensions.height.label',
      },
      lengthLabel: {
        defaultMessage: 'length',
        id: 'nemo.ui.sc.details.technical-data.dimensions.length.label',
      },
      widthLabel: {
        defaultMessage: 'width',
        id: 'nemo.ui.sc.details.technical-data.dimensions.width.label',
      },
      weightLabel: {
        defaultMessage: 'weight',
        id: 'nemo.ui.sc.details.technical-data.dimensions.weight.label',
      },
    },
  },
};

// eslint-disable-next-line no-empty-pattern
export const TechDataList: React.FC<TechDataListProps> = ({
  techDataItems,
  techDataKeys,
  isInitial,
  pollutionBadge,
  damages,
  dimensions,
  vehicle,
  dataTestId = '',
  showProductSafetyInfo,
}) => {
  const { getAdditionalService, featureAppConfig } = useServicesContext();
  const trackingService = getAdditionalService('audi-tracking-service') as TrackingServiceV2;
  const { language } = getAdditionalService('gfa:locale-service') as GfaLocaleServiceV1;
  const { defaultURL } = featureAppConfig as FeatureAppConfig;
  const country = vehicle.countryCode?.toLocaleLowerCase();
  const ASSET_URL = `${defaultURL}/img/environmental_labels/${country}/${pollutionBadge}.png`;
  const assetAltText = useI18n({
    id: `stockcars.pollutionBadge.${pollutionBadge?.replace('pollution-badge-type.', '')}.alt`,
    defaultMessage: '',
  });
  const pollutionBadgeLabel = useI18n(messages.technicalData.pollutionBadge);
  const repairsLabel = useI18n(messages.technicalData.damages.repairs.label);
  const repairsInfo = useI18n(messages.technicalData.damages.repairs.info);

  const { taxBandVed, exhaust } = vehicle;
  const taxBandVedLabel = useI18n(messages.technicalData.taxBandVed);

  const defectsLabel = useI18n(messages.technicalData.damages.defects.label);
  const repairs = damages ? damages?.repairs : [];
  const defects = damages ? damages?.defects : [];

  const heightLabel = useI18n(messages.technicalData.dimensions.heightLabel);
  const lengthLabel = useI18n(messages.technicalData.dimensions.lengthLabel);
  const widthLabel = useI18n(messages.technicalData.dimensions.widthLabel);
  const weightLabel = useI18n(messages.technicalData.dimensions.weightLabel);
  const exhaustLabel = useI18n(messages.technicalData.exhaust);

  const vehicleDimensions = dimensions || [];

  const vehicleHeight = vehicleDimensions.find((dimension) => dimension.key === 'height');
  const vehicleLength = vehicleDimensions.find((dimension) => dimension.key === 'length');
  const vehicleWidth = vehicleDimensions.find((dimension) => dimension.key === 'width');
  const vehicleWeight = vehicleDimensions.find((dimension) => dimension.key === 'weight_total');

  const carline = vehicle.symbolicCarline;
  const power = vehicle.powerDisplay.trim();
  const CarlineCode = {
    Q4SBetron: 'q4sbetron',
    Q4Etron: 'q4etron',
  };
  const Power = {
    kW250: '250 kW',
    kW210: '210 kW',
  };
  const q4nettoBatteryFootnote =
    (carline.code === CarlineCode.Q4SBetron || carline.code === CarlineCode.Q4Etron) &&
    (power === Power.kW250 || power === Power.kW210);

  const productSafetyInfo = techDataItems.find(
    (item) =>
      showProductSafetyInfo &&
      item.key === 'owners-manual-url' && // Check if the key equals "owners-manual-url"
      Boolean(item.name?.trim()) && // Ensure name is not null, undefined, or empty
      Boolean(item.value?.trim()) && // Ensure value is not null, undefined, or empty
      Boolean(item.details?.trim()) // Ensure details is valid or undefined
  );

  const visibleItems: Partial<TechdataItem>[] = [];

  // It fills up the visibleItems[] with available technical data
  // and inserts additional technical data about the vehicle dimensions.
  techDataKeys?.forEach((key) => {
    const entry =
      showProductSafetyInfo && !isInitial
        ? techDataItems.find((item) => item.key === key.fields.scsTechData)
        : techDataItems.find(
            (item) => item.key === key.fields.scsTechData && item.key !== 'owners-manual-url'
          );
    if (entry) {
      visibleItems.push(entry);
    } else if (vehicleDimensions.length) {
      switch (key.fields.scsTechData) {
        case 'vehicle-dimensions-height':
          if (vehicleHeight) {
            visibleItems.push({
              name: heightLabel,
              key: 'vehicle-dimensions-height',
              value: `${vehicleHeight.value} ${vehicleHeight.unit}`,
            });
          }
          break;
        case 'vehicle-dimensions-width':
          if (vehicleWidth) {
            visibleItems.push({
              name: widthLabel,
              key: 'vehicle-dimensions-width',
              value: `${vehicleWidth.value} ${vehicleWidth.unit}`,
            });
          }
          break;
        case 'vehicle-dimensions-length':
          if (vehicleLength) {
            visibleItems.push({
              name: lengthLabel,
              key: 'vehicle-dimensions-length',
              value: `${vehicleLength.value} ${vehicleLength.unit}`,
            });
          }
          break;
        case 'vehicle-dimensions-weight_total':
          if (vehicleWeight) {
            visibleItems.push({
              name: weightLabel,
              key: 'vehicle-dimensions-weight_total',
              value: `${vehicleWeight.value} ${vehicleWeight.unit}`,
            });
          }
          break;
        default:
      }
    }
  });

  const renderOwnersManualLink = () => {
    if (!productSafetyInfo) {
      return null;
    }

    return (
      <Item key={productSafetyInfo.key} data-testid="techDataItem">
        <Key>
          <Text tag="span" variant={{ type: 'copy', order: '1', style: 'bold' }}>
            <OwnersManualLabel>{productSafetyInfo.name}</OwnersManualLabel>
            <FootnoteReference refId={`fn_technicaldata_${productSafetyInfo.key}`} />
            {productSafetyInfo.key === 'battery-energy-content-net' && q4nettoBatteryFootnote && (
              <FootnoteReference refId="fn_battery-energy-content-net" />
            )}
          </Text>
        </Key>
        <Value>
          <TextLink
            variant="standalone"
            size="sm"
            icon="external-link"
            iconPosition="trailing"
            onClick={() => {
              trackOwnersManualClick(
                trackingService,
                productSafetyInfo.value,
                productSafetyInfo.details!
              );
              // this should be displayed as an internal link; internal links cannot have hrefs
              if (typeof window !== 'undefined') {
                window.open(productSafetyInfo.details!, '_blank');
              }
            }}
          >
            <Text tag="span" variant={{ type: 'copy', order: '1', style: 'normal' }}>
              {productSafetyInfo.value}
            </Text>
          </TextLink>
        </Value>
      </Item>
    );
  };

  return (
    <ListContainer data-testid={dataTestId}>
      <Column data-testid="techDataColumn" isInitial={isInitial}>
        {visibleItems
          ? visibleItems.map((item) => (
              <>
                {item.key !== 'owners-manual-url' && (
                  <Item key={item.key} data-testid="techDataItem">
                    <Key>
                      <Text
                        lang={language}
                        tag="p"
                        variant={{ type: 'copy', order: '1', style: 'bold' }}
                      >
                        {item.name}
                        <FootnoteReference refId={`fn_technicaldata_${item.key}`} />
                        {item.key === 'battery-energy-content-net' && q4nettoBatteryFootnote && (
                          <FootnoteReference refId="fn_battery-energy-content-net" />
                        )}
                      </Text>
                    </Key>
                    <Value>
                      <Text
                        lang={language}
                        tag="p"
                        variant={{ type: 'copy', order: '1', style: 'normal' }}
                      >
                        <TechDataFootnoteLabel
                          vehicle={vehicle}
                          techDataKey={item.key!}
                          techDataValue={item.value!}
                        />
                      </Text>
                    </Value>
                  </Item>
                )}
                {item.key === 'owners-manual-url' && !isInitial && <>{renderOwnersManualLink()}</>}
              </>
            ))
          : null}
      </Column>

      {isInitial && (
        <Column data-testid="techDataColumn" isInitial={isInitial}>
          {pollutionBadge && (
            <Item>
              <Key>
                <Text
                  lang={language}
                  tag="span"
                  variant={{ type: 'copy', order: '1', style: 'bold' }}
                >
                  {pollutionBadgeLabel}
                </Text>
              </Key>
              <Value>
                <Text
                  lang={language}
                  tag="span"
                  variant={{ type: 'copy', order: '1', style: 'normal' }}
                >
                  <Badge src={`${ASSET_URL}`} data-testid="pollutionBadge" alt={assetAltText} />
                </Text>
              </Value>
            </Item>
          )}
          {exhaust && (
            <Item>
              <Key>
                <Text
                  tag="span"
                  lang={language}
                  variant={{ type: 'copy', order: '1', style: 'bold' }}
                >
                  {exhaustLabel}
                </Text>
              </Key>
              <Value>
                <Text
                  tag="span"
                  lang={language}
                  variant={{ type: 'copy', order: '1', style: 'normal' }}
                >
                  {exhaust}
                </Text>
              </Value>
            </Item>
          )}
          {taxBandVed && (
            <Item>
              <Key>
                <Text
                  tag="span"
                  lang={language}
                  variant={{ type: 'copy', order: '1', style: 'bold' }}
                >
                  {taxBandVedLabel}
                </Text>
              </Key>
              <Value>
                <Text
                  tag="span"
                  lang={language}
                  variant={{ type: 'copy', order: '1', style: 'normal' }}
                >
                  {taxBandVed}
                </Text>
              </Value>
            </Item>
          )}
          {repairs?.map((item, index) => (
            <Item key={`repairs-${(index * Math.random()).toString()}`}>
              <Key>
                <Text
                  tag="span"
                  lang={language}
                  variant={{ type: 'copy', order: '1', style: 'bold' }}
                  data-testid="repairItem"
                >
                  {index === 0 ? repairsLabel : ''}
                </Text>
              </Key>
              <Value>
                <Text tag="p" lang={language} variant={{ type: 'copy', order: '1', style: 'bold' }}>
                  {item}
                </Text>
                <Text
                  tag="p"
                  lang={language}
                  variant={{ type: 'copy', order: '1', style: 'normal' }}
                >
                  {repairsInfo}
                </Text>
              </Value>
            </Item>
          ))}
          {defects?.map((item, index) => (
            <Item key={`defects-${index.toString()}`}>
              <Key>
                <Text
                  tag="span"
                  lang={language}
                  variant={{ type: 'copy', order: '1', style: 'bold' }}
                >
                  {index === 0 ? defectsLabel : ''}
                </Text>
              </Key>
              <Value>
                <Text
                  tag="p"
                  lang={language}
                  variant={{ type: 'copy', order: '1', style: 'normal' }}
                >
                  {item}
                </Text>
              </Value>
            </Item>
          ))}
          {renderOwnersManualLink()}
        </Column>
      )}
    </ListContainer>
  );
};

const OwnersManualLabel = styled.span`
  display: inline-block;
  transform: translateY(0.05em);
`;
